import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { isSuperAdmin, normalizeRole } from "../../lib/features";

const BLOCKED_MESSAGES = {
  inactive: "!",
  blocked: "!",
  cancelled: "x",
};

const BLOCKED_ICONS = {
  inactive: "Sua conta está temporariamente inativa. Entre em contato com o suporte.",
  blocked: "?",
  cancelled: "x",
};

/** Tela exibida quando o tenant está inativo, bloqueado ou cancelado. */
function TenantBlockedView({ status }) {
  const { logout } = useAuth();
  const msg = BLOCKED_MESSAGES[status] || "Acesso negado.";
  const icon = BLOCKED_ICONS[status] || "?";

  return (
    <div className="h-screen w-screen flex items-center justify-center bg-slate-950">
      <div className="max-w-md w-full mx-4 text-center p-8 bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl">
        <div className="text-5xl mb-4">{icon}</div>
        <h2 className="text-xl font-semibold text-slate-100 mb-2">Acesso Restrito</h2>
        <p className="text-slate-400 mb-8 leading-relaxed">{msg}</p>
        <button
          onClick={logout}
          className="px-5 py-2.5 bg-slate-700 text-slate-100 rounded-lg hover:bg-slate-600 transition-colors text-sm font-medium"
        >
          Voltar ao Login
        </button>
      </div>
    </div>
  );
}

/** Protege qualquer rota que exige autenticação.
 *  Verifica também tenant_status - exibe TenantBlockedView para contas restritas.
 *  super_admin nunca é bloqueado por status.
 */
export default function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-slate-950">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-[#25D366] border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-400 text-sm">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user || user === false) return <Navigate to="/login" replace />;

  const isBlocked =
    !isSuperAdmin(user) &&
    user.tenant_status &&
    !["active", "trial"].includes(user.tenant_status);

  if (isBlocked) return <TenantBlockedView status={user.tenant_status} />;

  return children;
}

/**
 * Protects admin-only routes.
 * Agents are redirected to / with an access-denied toast.
 * Must be nested inside <ProtectedRoute> so `user` is guaranteed to be set.
 */
export function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return null;

  if (user && !["owner", "admin", "super_admin"].includes(normalizeRole(user.role))) {
    toast.error("Acesso negado. Esta área é exclusiva para administradores.", {
      id: "access-denied",
    });
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  return children;
}

/**
 * Protects super_admin-only routes (e.g. /admin).
 * Must be nested inside <ProtectedRoute>.
 */
export function SuperAdminRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return null;

  if (user && !isSuperAdmin(user)) {
    toast.error("Acesso negado. Esta área é exclusiva para super administradores.", {
      id: "super-admin-denied",
    });
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  return children;
}
